import React, { useContext, useEffect } from 'react';
import type { FC, PropsWithChildren } from 'react';
import Box from '@mui/material/Box';

import AppLayoutContainer from '../AppLayoutContainer/AppLayoutContainer';
import ContentContainer from '../ContentContainer/ContentContainer';
import NotificationAlertWrapper from '../NotificationAlertWrapper/NotificationAlertWrapper';
import { AppContext } from '../../capabilities/contextStore/AppContext';
import { settingsApi } from '../../services/settingsApi/settingsApi';
import { appInsights } from '../../services/AppInsights/app-insights';
import getCookie from '../../utils/function/getCookie';
import { DEFAULT_ENV } from '../../utils/constants';
import { NotificationContext } from '../../capabilities/contextStore/NotificationContext';
import { httpClientService } from '../../services/settingsApi/httpClientService';
import ConditionalOverlayLoader from '../OverlayLoader/ConditionalOverlayLoader';
import CancelSessionHandler from '../../components/cancel-session-handler/CancelSessionHandler';
import useParseCheckoutSessionId from '../../shared/CheckoutSessionInitializer/useParseCheckoutSessionId';
import ConditionalHeader from '../../components/Header/ConditionalHeader';
import connectionChecker from '../../utils/connection/connection-checker/ConnectionChecker';
import onErrorHandler from '../../services/shared/onErrorHandler';
import ConditionalAppContentVisibility from '../../components/conditional-app-content-visibility/ConditionalAppContentVisibility';
import useEnvironmentContextProvider from '../../capabilities/contextStore/EnvironmentContext/useEnvironmentContextProvider';
import type { AppEnv } from '../../shared/types/app/AppEnv';

export interface AppContainerProps {
  onCancel: () => void;
}

const AppContainer: FC<PropsWithChildren<AppContainerProps>> = ({
  children,
  onCancel,
}) => {
  const {
    isAuthorized,
    setIsAuthorized,
    setAppMetadata,
    setData,
    isChildWaitingOnGlobalSession,
    isSessionOutcomeShowingDeadEndScreen,
  } = useContext(AppContext);
  const { setEnvironmentState } =
    useEnvironmentContextProvider();

  const checkoutSessionId = useParseCheckoutSessionId();

  const { notify } = useContext(NotificationContext);

  const getAppEnv = ({
    searchParams,
  }: {
    searchParams: URLSearchParams;
  }): AppEnv =>
    ((searchParams.get('appEnv') ||
      getCookie('ccg-app-env')) as AppEnv) || DEFAULT_ENV;

  const queryParams = new URLSearchParams(
    window.location.search,
  );
  const appEnv = getAppEnv({ searchParams: queryParams });

  useEffect(() => {
    setData({ overlayLoaderConfig: { show: true } });

    httpClientService.init({
      appEnv,
      onUnAuthorizedCallback: () => {
        setIsAuthorized(false);
        setData({ overlayLoaderConfig: { show: false } });
      },
      onError: onErrorHandler({ notify, setData }),
    });

    if (checkoutSessionId) {
      setAppMetadata({
        checkoutSessionId,
      });
      setData({ appEnv });
    }
    if (!checkoutSessionId) {
      setData({ overlayLoaderConfig: { show: false } });
    }

    (async () => {
      const aiConfig = await settingsApi.getAIConfig();
      appInsights.init(aiConfig);
    })().catch((error: unknown) => {
      console.error(
        `Error when trying to get ai connection string: ${String(error)}`,
      );
    });

    setEnvironmentState({
      onDoneClick: undefined,
    });

    return () => {
      connectionChecker.stopChecking();
    };
  }, []);

  return (
    <AppLayoutContainer>
      <ConditionalHeader />
      {isAuthorized &&
      !isChildWaitingOnGlobalSession &&
      !isSessionOutcomeShowingDeadEndScreen ? (
        <Box style={{ padding: '0 20px 20px 20px' }}>
          <NotificationAlertWrapper />
        </Box>
      ) : null}

      <ContentContainer>
        <ConditionalOverlayLoader />
        <ConditionalAppContentVisibility>
          <CancelSessionHandler
            sessionId={checkoutSessionId}
            onCancelEnvironmentCallback={onCancel}
          >
            {children}
          </CancelSessionHandler>
        </ConditionalAppContentVisibility>
      </ContentContainer>
    </AppLayoutContainer>
  );
};

export default AppContainer;
